import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '@mysimpledb/shared/utils/cropImageHelper';

const CropUploader = ({
  onCropComplete,
  aspect = 1,
  buttonLabel = 'Use this photo',
  maxFileSize = 5 * 1024 * 1024,
  cropperWidth = 300,
  cropperHeight = 300,
  showFitControls = false,
  fitLabel = 'Fit entire image',
  fillLabel = 'Fill crop area',
  minZoom = 1,
  maxZoom = 3,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [origFileName, setOrigFileName] = useState();
  const [error, setError] = useState('');
  const [fitMode, setFitMode] = useState('contain');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    setError('');

    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setError('Choose a JPG or PNG image.');
      return;
    }

    if (file.size > maxFileSize) {
      setError('Image must be 5 MB or smaller.');
      return;
    }

    const imageDataUrl = await readFile(file);
    setOrigFileName(file.name);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setFitMode(showFitControls ? 'contain' : 'cover');
    setImageSrc(imageDataUrl);
  };

  const readFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result));
      reader.readAsDataURL(file);
    });
  };

  const onCropCompleteHandler = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const finishCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
    onCropComplete({
      blob: croppedBlob,
      name: origFileName || 'image.jpg',
    });
    setImageSrc(null);
    setOrigFileName('');
  };

  return (
    <div>
      <input
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileChange}
      />
      {error && <div role="alert">{error}</div>}
      {imageSrc && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: cropperWidth,
            height: cropperHeight,
            background: '#d1d5db',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            aspect={aspect}
            objectFit={fitMode}
            restrictPosition={!showFitControls}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropCompleteHandler}
          />
        </div>
      )}
      {imageSrc && showFitControls && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 10,
          }}
        >
          <button
            type="button"
            aria-pressed={fitMode === 'contain'}
            onClick={() => {
              setFitMode('contain');
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
          >
            {fitLabel}
          </button>
          <button
            type="button"
            aria-pressed={fitMode === 'cover'}
            onClick={() => {
              setFitMode('cover');
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
          >
            {fillLabel}
          </button>
        </div>
      )}
      {imageSrc && showFitControls && (
        <div style={{ marginTop: 10, maxWidth: cropperWidth }}>
          <label
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(140px, 1fr) auto',
              gap: 10,
              alignItems: 'center',
              margin: 0,
            }}
          >
            <span>Zoom</span>
            <input
              type="range"
              min={minZoom}
              max={maxZoom}
              step={0.05}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
            <span>{zoom.toFixed(2)}×</span>
          </label>

          <button
            type="button"
            style={{ marginTop: 10 }}
            onClick={finishCrop}
          >
            {buttonLabel}
          </button>
        </div>
      )}
      {imageSrc && !showFitControls && (
        <button type="button" onClick={finishCrop}>
          {buttonLabel}
        </button>
      )}
    </div>
  );
};

export default CropUploader;
